/**
 * React Query hooks for Connection Management
 *
 * Provides hooks for:
 * - Listing connections
 * - Getting single connection
 * - Creating connections
 * - Updating connections
 * - State transitions (pause/unpause/archive/restore)
 * - Deleting and undeleting connections
 * - OAuth flow initiation and reconnection
 *
 * Based on CONNECTION_STATE_INTERFACE.md specification (Model B)
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query'
import { message } from 'antd'
import { connectionService } from '@/services/connectionService'
import type {
  Connection,
  CreateConnectionRequest,
  OAuthInitiateRequest,
  OAuthInitiateResponse,
  ReconnectRequest,
  ReconnectResponse,
  TestConnectionResult,
} from '@/types/connection.types'

/**
 * Query key factory for connections
 * Centralizes query key management for consistent cache invalidation
 */
export const connectionKeys = {
  all: ['connections'] as const,
  lists: () => [...connectionKeys.all, 'list'] as const,
  list: (filters?: string) => [...connectionKeys.lists(), { filters }] as const,
  details: () => [...connectionKeys.all, 'detail'] as const,
  detail: (id: string) => [...connectionKeys.details(), id] as const,
}

/**
 * Hook to list all connections for current organization
 *
 * Features:
 * - Automatic caching with 30-second stale time (health status changes frequently)
 * - Background refetching when stale
 * - Refetch on window focus (user returns to tab)
 * - Shared cache across all components using connections list
 * - Can be disabled when org context is not available (prevents RLS errors)
 *
 * @param options - Query options (enabled, etc.)
 * @returns React Query result with connections array
 *
 * @example
 * ```tsx
 * function ConnectionsList() {
 *   const { data: user } = useCurrentUser()
 *   const hasOrg = !!user?.currentOrganization
 *
 *   // Disable query when no org is selected (prevents RLS errors)
 *   const { data: connections, isLoading, error } = useConnections({ enabled: hasOrg })
 *
 *   if (!hasOrg) return <NoOrgSelectedPrompt featureName="connections" />
 *   if (isLoading) return <Spin />
 *   if (error) return <Alert message="Failed to load connections" type="error" />
 *
 *   return (
 *     <div>
 *       {connections?.map(conn => (
 *         <ConnectionCard key={conn.uid} connection={conn} />
 *       ))}
 *     </div>
 *   )
 * }
 * ```
 */
export function useConnections(
  options?: { enabled?: boolean }
): UseQueryResult<Connection[], Error> {
  return useQuery({
    queryKey: connectionKeys.lists(),
    queryFn: async () => {
      const response = await connectionService.list()

      if (!response.success || !response.data) {
        throw new Error(response.errors?.[0]?.message || 'Failed to load connections')
      }

      return response.data
    },
    enabled: options?.enabled !== false,
    staleTime: 1000 * 30, // 30 seconds - health status updates frequently
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 60, // Refetch every minute for health status updates
  })
}

/**
 * Hook to get single connection by UID
 *
 * @param uid - Connection UID
 * @param options - Query options (enabled, etc.)
 *
 * @returns React Query result with connection details
 *
 * @example
 * ```tsx
 * function ConnectionDetails({ connectionId }: { connectionId: string }) {
 *   const { data: connection, isLoading } = useConnection(connectionId)
 *
 *   if (isLoading) return <Spin />
 *   if (!connection) return <Alert message="Connection not found" type="warning" />
 *
 *   return <ConnectionCard connection={connection} />
 * }
 * ```
 */
export function useConnection(
  uid: string,
  options?: { enabled?: boolean }
): UseQueryResult<Connection, Error> {
  return useQuery({
    queryKey: connectionKeys.detail(uid),
    queryFn: async () => {
      const response = await connectionService.get(uid)

      if (!response.success || !response.data) {
        throw new Error(response.errors?.[0]?.message || 'Failed to load connection')
      }

      return response.data
    },
    enabled: options?.enabled !== false,
    staleTime: 1000 * 30, // 30 seconds
    refetchOnWindowFocus: true,
  })
}

/**
 * Hook to create new connection
 *
 * Features:
 * - Automatic cache invalidation (refetches connections list)
 * - Optimistic updates for instant UI feedback
 * - Success/error notifications via Ant Design message
 *
 * @returns Mutation function and state
 *
 * @example
 * ```tsx
 * function CreateConnectionButton() {
 *   const createConnection = useCreateConnection()
 *
 *   const handleCreate = async () => {
 *     const result = await createConnection.mutateAsync({
 *       name: 'Square Production',
 *       platform: 'square',
 *       description: 'Main POS connection'
 *     })
 *
 *     if (result.uid) {
 *       // Connection created, now initiate OAuth
 *       navigate(`/connections/${result.uid}/oauth`)
 *     }
 *   }
 *
 *   return (
 *     <Button
 *       onClick={handleCreate}
 *       loading={createConnection.isPending}
 *     >
 *       Create Connection
 *     </Button>
 *   )
 * }
 * ```
 */
export function useCreateConnection(): UseMutationResult<
  Connection,
  Error,
  CreateConnectionRequest
> {
  return useMutation({
    mutationFn: async (request: CreateConnectionRequest) => {
      const response = await connectionService.create(request)

      if (!response.success || !response.data) {
        throw new Error(response.errors?.[0]?.message || 'Failed to create connection')
      }

      return response.data
    },
    // NOTE: No onSuccess handler here intentionally!
    // The OAuth flow redirects immediately after creation.
    // Success feedback is shown after OAuth callback completes.
    // Cache invalidation happens when user returns from OAuth.
    onError: (error: Error) => {
      message.error(`Failed to create connection: ${error.message}`)
    },
  })
}

/**
 * Hook to update connection details (name, description)
 *
 * @returns Mutation function and state
 *
 * @example
 * ```tsx
 * const updateConnection = useUpdateConnection()
 *
 * await updateConnection.mutateAsync({
 *   uid: '550e8400...',
 *   updates: { name: 'Square Production - Main Store' },
 *   version: 5
 * })
 * ```
 */
export function useUpdateConnection(): UseMutationResult<
  Connection,
  Error,
  { uid: string; updates: Partial<Pick<Connection, 'name' | 'description'>>; version: number }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ uid, updates, version }) => {
      const response = await connectionService.update(uid, updates, version)

      if (!response.success || !response.data) {
        throw new Error(response.errors?.[0]?.message || 'Failed to update connection')
      }

      return response.data
    },
    onSuccess: (updatedConnection) => {
      // Update both list and detail caches
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() })
      queryClient.invalidateQueries({ queryKey: connectionKeys.detail(updatedConnection.uid) })

      message.success('Connection updated successfully')
    },
    onError: (error: Error) => {
      message.error(`Failed to update connection: ${error.message}`)
    },
  })
}

/**
 * Hook to pause connection (active → paused)
 *
 * @returns Mutation function and state
 *
 * @example
 * ```tsx
 * const pauseConnection = usePauseConnection()
 *
 * await pauseConnection.mutateAsync('550e8400...')
 * ```
 */
export function usePauseConnection(): UseMutationResult<Connection, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (uid: string) => {
      const response = await connectionService.pause(uid)

      if (!response.success || !response.data) {
        throw new Error(response.errors?.[0]?.message || 'Failed to pause connection')
      }

      return response.data
    },
    onSuccess: (updatedConnection) => {
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() })
      queryClient.invalidateQueries({ queryKey: connectionKeys.detail(updatedConnection.uid) })

      message.success('Connection paused')
    },
    onError: (error: Error) => {
      message.error(`Failed to pause connection: ${error.message}`)
    },
  })
}

/**
 * Hook to unpause connection (paused → active)
 */
export function useUnpauseConnection(): UseMutationResult<Connection, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (uid: string) => {
      const response = await connectionService.unpause(uid)

      if (!response.success || !response.data) {
        throw new Error(response.errors?.[0]?.message || 'Failed to unpause connection')
      }

      return response.data
    },
    onSuccess: (updatedConnection) => {
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() })
      queryClient.invalidateQueries({ queryKey: connectionKeys.detail(updatedConnection.uid) })

      message.success('Connection resumed')
    },
    onError: (error: Error) => {
      message.error(`Failed to unpause connection: ${error.message}`)
    },
  })
}

/**
 * Hook to archive connection (active/paused → archived)
 */
export function useArchiveConnection(): UseMutationResult<Connection, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (uid: string) => {
      const response = await connectionService.archive(uid)

      if (!response.success || !response.data) {
        throw new Error(response.errors?.[0]?.message || 'Failed to archive connection')
      }

      return response.data
    },
    onSuccess: (updatedConnection) => {
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() })
      queryClient.invalidateQueries({ queryKey: connectionKeys.detail(updatedConnection.uid) })

      message.success('Connection archived')
    },
    onError: (error: Error) => {
      message.error(`Failed to archive connection: ${error.message}`)
    },
  })
}

/**
 * Hook to restore archived connection (archived → active)
 */
export function useRestoreConnection(): UseMutationResult<Connection, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (uid: string) => {
      const response = await connectionService.restore(uid)

      if (!response.success || !response.data) {
        throw new Error(response.errors?.[0]?.message || 'Failed to restore connection')
      }

      return response.data
    },
    onSuccess: (updatedConnection) => {
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() })
      queryClient.invalidateQueries({ queryKey: connectionKeys.detail(updatedConnection.uid) })

      message.success('Connection restored')
    },
    onError: (error: Error) => {
      message.error(`Failed to restore connection: ${error.message}`)
    },
  })
}

/**
 * Hook to delete connection (soft delete - sets deletedAt)
 *
 * Note: DELETE returns 204 No Content with null data, so we only check success.
 * We pass the uid through for cache invalidation.
 */
export function useDeleteConnection(): UseMutationResult<string, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (uid: string) => {
      const response = await connectionService.delete(uid)

      // DELETE returns 204 No Content with null data - only check success
      if (!response.success) {
        throw new Error(response.errors?.[0]?.message || 'Failed to delete connection')
      }

      // Return the uid for cache invalidation in onSuccess
      return uid
    },
    onSuccess: (deletedUid) => {
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() })
      queryClient.invalidateQueries({ queryKey: connectionKeys.detail(deletedUid) })

      message.success('Connection deleted')
    },
    onError: (error: Error) => {
      message.error(`Failed to delete connection: ${error.message}`)
    },
  })
}

/**
 * Hook to undelete connection (restore from soft delete - clears deletedAt)
 */
export function useUndeleteConnection(): UseMutationResult<Connection, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (uid: string) => {
      const response = await connectionService.undelete(uid)

      if (!response.success || !response.data) {
        throw new Error(response.errors?.[0]?.message || 'Failed to undelete connection')
      }

      return response.data
    },
    onSuccess: (updatedConnection) => {
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() })
      queryClient.invalidateQueries({ queryKey: connectionKeys.detail(updatedConnection.uid) })

      message.success('Connection restored from deletion')
    },
    onError: (error: Error) => {
      message.error(`Failed to undelete connection: ${error.message}`)
    },
  })
}

/**
 * Hook to initiate OAuth flow
 *
 * NOTE: This hook does NOT redirect to OAuth URL automatically.
 * Caller must handle redirect after successful response.
 *
 * @returns Mutation function and state
 *
 * @example
 * ```tsx
 * const initiateOAuth = useInitiateOAuth()
 *
 * const handleConnect = async (connectionUid: string) => {
 *   const result = await initiateOAuth.mutateAsync({
 *     connectionUid,
 *     redirectUri: window.location.origin + '/oauth/callback'
 *   })
 *
 *   // Store CSRF state for validation
 *   sessionStorage.setItem('oauth_state', result.state)
 *
 *   // Redirect to platform OAuth page
 *   window.location.href = result.authorizationUrl
 * }
 * ```
 */
export function useInitiateOAuth(): UseMutationResult<
  OAuthInitiateResponse,
  Error,
  OAuthInitiateRequest
> {
  return useMutation({
    mutationFn: async (request: OAuthInitiateRequest) => {
      const response = await connectionService.initiateOAuth(request)

      if (!response.success || !response.data) {
        throw new Error(response.errors?.[0]?.message || 'Failed to initiate OAuth flow')
      }

      return response.data
    },
    onError: (error: Error) => {
      message.error(`Failed to start connection: ${error.message}`)
    },
  })
}

/**
 * Hook to reconnect expired connection (re-initiate OAuth)
 *
 * NOTE: This hook does NOT redirect to OAuth URL automatically.
 * Caller must handle redirect after successful response.
 *
 * @returns Mutation function and state
 *
 * @example
 * ```tsx
 * const reconnect = useReconnectConnection()
 *
 * const handleReconnect = async (connectionUid: string) => {
 *   const result = await reconnect.mutateAsync({
 *     uid: connectionUid,
 *     request: { redirectUri: window.location.origin + '/oauth/callback' }
 *   })
 *
 *   // Store CSRF state for validation
 *   sessionStorage.setItem('oauth_state', result.csrfToken)
 *
 *   // Redirect to platform OAuth page
 *   window.location.href = result.authUrl
 * }
 * ```
 */
export function useReconnectConnection(): UseMutationResult<
  ReconnectResponse,
  Error,
  { uid: string; request: ReconnectRequest }
> {
  return useMutation({
    mutationFn: async ({ uid, request }) => {
      const response = await connectionService.reconnect(uid, request)

      if (!response.success || !response.data) {
        throw new Error(response.errors?.[0]?.message || 'Failed to reconnect')
      }

      return response.data
    },
    onError: (error: Error) => {
      message.error(`Failed to reconnect: ${error.message}`)
    },
  })
}

/**
 * Hook to refresh OAuth tokens for a connection
 *
 * Manually triggers token refresh before expiry. Useful for:
 * - Demonstrating Square certification compliance (7-day refresh)
 * - Testing token refresh mechanism
 * - Proactively refreshing tokens that will expire soon
 *
 * @returns Mutation function and state
 *
 * @example
 * ```tsx
 * const refreshConnection = useRefreshConnection()
 *
 * const handleRefresh = async (connectionUid: string) => {
 *   await refreshConnection.mutateAsync(connectionUid)
 *   // Token expires time will be updated in connection
 * }
 * ```
 */
export function useRefreshConnection(): UseMutationResult<Connection, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (uid: string) => {
      const response = await connectionService.refresh(uid)

      if (!response.success || !response.data) {
        throw new Error(response.errors?.[0]?.message || 'Failed to refresh tokens')
      }

      return response.data
    },
    onSuccess: (updatedConnection) => {
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() })
      queryClient.invalidateQueries({ queryKey: connectionKeys.detail(updatedConnection.uid) })

      message.success('Tokens refreshed successfully')
    },
    onError: (error: Error) => {
      message.error(`Failed to refresh tokens: ${error.message}`)
    },
  })
}

/**
 * Hook to test connection health
 *
 * Verifies connection can communicate with platform API.
 * Returns test result with health status and account info.
 *
 * @returns Mutation function and state
 *
 * @example
 * ```tsx
 * const testConnection = useTestConnection()
 *
 * const handleTest = async (connectionUid: string) => {
 *   const result = await testConnection.mutateAsync(connectionUid)
 *   console.log('Account:', result.accountInfo)
 * }
 * ```
 */
export function useTestConnection(): UseMutationResult<TestConnectionResult, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (uid: string) => {
      const response = await connectionService.test(uid)

      if (!response.success || !response.data) {
        throw new Error(response.errors?.[0]?.message || 'Failed to test connection')
      }

      return response.data
    },
    onSuccess: (testResult) => {
      // Invalidate connection queries to refresh health status
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() })
      queryClient.invalidateQueries({ queryKey: connectionKeys.detail(testResult.connectionUid) })

      // Don't show toast here - let the caller decide (e.g., show modal instead)
    },
    onError: (error: Error) => {
      message.error(`Failed to test connection: ${error.message}`)
    },
  })
}
